// src/pages/ReadjudicacionMasiva/LoteGenerarDocumentos.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Box,
    Paper,
    IconButton,
    Grid,
    Button,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stepper,
    Step,
    StepLabel,
    Chip
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Description as DescriptionIcon,
    CheckCircle as CheckCircleIcon,
    GetApp as GetAppIcon,
    Print as PrintIcon,
    Link as LinkIcon,
    Receipt as ReceiptIcon,
    Notifications as NotificationsIcon,
    SwapHoriz as SwapHorizIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import useReadjudicacion from '../../hooks/useReadjudicacion';
import { usePermisos } from '../../utils/usePermisos';
import TipoDocumentoSelector from '../../components/ReadjudicacionMasiva/Documentos/TipoDocumentoSelector';
import OpcionesImpresion from '../../components/ReadjudicacionMasiva/Documentos/OpcionesImpresion';
import ResumenGeneracion from '../../components/ReadjudicacionMasiva/Documentos/ResumenGeneracion';

const styles = {
    root: {
        backgroundColor: '#f6f8fc',
        minHeight: '100vh',
        paddingTop: '24px',
        paddingBottom: '24px'
    },
    header: {
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
    },
    title: {
        fontWeight: 700,
        color: '#5c6bc0',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flex: 1
    },
    loteInfo: {
        padding: '16px',
        marginBottom: '24px',
        backgroundColor: '#fafafa'
    },
    section: {
        marginBottom: '24px'
    },
    sectionTitle: {
        fontWeight: 600,
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    loadingBox: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 400
    },
    estadoChip: {
        marginLeft: '8px'
    },
    actionsBar: {
        marginTop: '24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '16px'
    },
    stepper: {
        backgroundColor: 'transparent',
        padding: '24px 0px'
    }
};

const steps = [
    'Seleccionar Documentos',
    'Configurar Opciones',
    'Generar y Descargar'
];

const LoteGenerarDocumentos = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { tienePermiso } = usePermisos();

    const {
        loading,
        lote,
        registros,
        obtenerLote,
        listarRegistros,
        generarDocumentos,
        finalizarReadjudicacion
    } = useReadjudicacion();

    const [activeStep, setActiveStep] = useState(0);
    const [documentosSeleccionados, setDocumentosSeleccionados] = useState([]);
    const [opcionesImpresion, setOpcionesImpresion] = useState({
        formato: 'PDF',
        ordenamiento: 'FILA',
        copias: 1,
        orientacion: 'VERTICAL',
        observaciones: ''
    });
    const [generando, setGenerando] = useState(false);
    const [progreso, setProgreso] = useState(0);
    const [confirmDialog, setConfirmDialog] = useState({ open: false });
    const [documentosGenerados, setDocumentosGenerados] = useState([]);
    const [resumenFinalizacion, setResumenFinalizacion] = useState(null);

    // Verificar permisos
    useEffect(() => {
        if (!tienePermiso('READJUDICACION MASIVA', 'UPDATE')) {
            enqueueSnackbar('No tiene permisos para generar documentos', { variant: 'error' });
            navigate('/gestion-lotes?tab=readjudicacion');
        }
    }, [tienePermiso, navigate, enqueueSnackbar]);

    // Cargar datos del lote
    useEffect(() => {
        if (id) {
            cargarDatos();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Estado para controlar si el lote está finalizado (solo lectura)
    const loteFinalizado = lote && lote.Estado === 5;

    const cargarDatos = async () => {
        await Promise.all([
            obtenerLote(id),
            listarRegistros(id)
        ]);
    };

    const handleNext = () => {
        if (activeStep === 0 && documentosSeleccionados.length === 0) {
            enqueueSnackbar('Debe seleccionar al menos un tipo de documento', { variant: 'warning' });
            return;
        }
        setActiveStep((prevStep) => prevStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    const handleGenerarDocumentos = async () => {
        setGenerando(true);
        setProgreso(0);

        // Simular progreso
        const progressInterval = setInterval(() => {
            setProgreso((prev) => {
                if (prev >= 90) {
                    clearInterval(progressInterval);
                    return 90;
                }
                return prev + 10;
            });
        }, 500);

        try {
            const resultado = await generarDocumentos(
                id,
                documentosSeleccionados,
                opcionesImpresion
            );

            clearInterval(progressInterval);
            setProgreso(100);

            if (resultado && resultado.ok) {
                enqueueSnackbar(`${resultado.cantidad} documento(s) generado(s) correctamente`, { variant: 'success' });

                // Si hay documentos renderizados, guardarlos para visualizacion
                if (resultado.documentos && resultado.documentos.length > 0) {
                    setDocumentosGenerados(resultado.documentos);
                }

                // Mostrar dialog de confirmación de finalización
                setConfirmDialog({ open: true });
            }
        } catch (error) {
            clearInterval(progressInterval);
            setProgreso(0);
            enqueueSnackbar('Error al generar documentos', { variant: 'error' });
        } finally {
            setGenerando(false);
        }
    };


    const handleFinalizarLote = async () => {
        // Ejecutar el pasaje completo: movimientos + actualización de Partes + cambio de estado
        const resultado = await finalizarReadjudicacion(id);
        if (resultado && resultado.ok) {
            setResumenFinalizacion(resultado);
        }
        setConfirmDialog({ open: false });
    };

    const handleVolver = () => {
        navigate('/gestion-lotes?tab=readjudicacion');
    };

    const getEstadoChip = () => {
        if (!lote) return null;

        const estados = {
            0: { label: 'Pendiente Auditoría', color: 'warning' },
            1: { label: 'Auditado', color: 'primary' },
            2: { label: 'Desapoderado', color: 'secondary' },
            3: { label: 'Asignado', color: 'info' },
            4: { label: 'Listo para Pasaje', color: 'warning' },
            5: { label: 'Finalizado', color: 'success' }
        };

        const estado = estados[lote.Estado] || { label: 'Desconocido', color: 'warning' };
        return <Chip label={estado.label} color={estado.color} size="small" sx={styles.estadoChip} />;
    };

    const registrosValidos = registros.filter(r => r.Ok === 1);

    const cantidadesPorDocumento = documentosSeleccionados.reduce((acc, tipo) => {
        acc[tipo] = registrosValidos.length;
        return acc;
    }, {});

    if (loading && !lote) {
        return (
            <Container maxWidth={false} sx={styles.root}>
                <Box sx={styles.loadingBox}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (!lote) {
        return (
            <Container maxWidth={false} sx={styles.root}>
                <Typography variant="h6" color="error">
                    Lote no encontrado
                </Typography>
            </Container>
        );
    }

    // Mostrar pantalla de solo lectura si el lote está finalizado
    if (loteFinalizado) {
        return (
            <Container maxWidth={false} sx={styles.root}>
                {/* Header */}
                <Box sx={styles.header}>
                    <IconButton onClick={handleVolver} size="small" style={{ color: '#5c6bc0' }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h4" style={{ fontWeight: 700, color: '#5c6bc0', display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                        <CheckCircleIcon fontSize="large" />
                        Lote #{lote.Id} - Finalizado
                    </Typography>
                    <Chip label="Finalizado" color="primary" style={{ backgroundColor: '#4caf50' }} />
                </Box>

                {/* Información del lote */}
                <Paper sx={styles.loteInfo}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Typography variant="caption" color="text.secondary">
                                Expediente Administrativo
                            </Typography>
                            <Typography variant="body1" style={{ fontWeight: 600 }}>
                                {lote.ExpedienteAdministrativo || '-'}
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Typography variant="caption" color="text.secondary">
                                Total Registros
                            </Typography>
                            <Typography variant="body1" style={{ fontWeight: 600 }}>
                                {lote.TotalRegistros || 0}
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Typography variant="caption" color="text.secondary">
                                Registros Procesados
                            </Typography>
                            <Typography variant="body1" style={{ fontWeight: 600, color: '#4caf50' }}>
                                {registrosValidos.length}
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Typography variant="caption" color="text.secondary">
                                Fecha Finalizado
                            </Typography>
                            <Typography variant="body1" style={{ fontWeight: 600 }}>
                                {lote.Fecha ? new Date(lote.Fecha).toLocaleDateString('es-AR') : '-'}
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Mensaje de lote finalizado */}
                <Paper style={{ padding: 32, textAlign: 'center' }}>
                    <CheckCircleIcon style={{ fontSize: 80, color: '#4caf50', marginBottom: 16 }} />
                    <Typography variant="h5" gutterBottom style={{ fontWeight: 600 }}>
                        Este lote ha sido finalizado
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                        Los documentos ya fueron generados y el proceso de readjudicacion se ha completado exitosamente.
                        Los movimientos y cambios en las partes ya fueron aplicados a los expedientes correspondientes.
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleVolver}
                        startIcon={<ArrowBackIcon />}
                        style={{ marginTop: 16 }}
                    >
                        Volver al Dashboard
                    </Button>
                </Paper>
            </Container>
        );
    }

    const getStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <Box>
                        <Typography variant="subtitle1" sx={styles.sectionTitle}>
                            <DescriptionIcon />
                            Seleccione los tipos de documentos a generar
                        </Typography>
                        <TipoDocumentoSelector
                            seleccionados={documentosSeleccionados}
                            onChange={setDocumentosSeleccionados}
                            cantidades={cantidadesPorDocumento}
                        />
                    </Box>
                );
            case 1:
                return (
                    <Box>
                        <Typography variant="subtitle1" sx={styles.sectionTitle}>
                            <PrintIcon />
                            Configure las opciones de generación
                        </Typography>
                        <OpcionesImpresion
                            opciones={opcionesImpresion}
                            onChange={setOpcionesImpresion}
                        />
                    </Box>
                );
            case 2:
                return (
                    <Box>
                        <Typography variant="subtitle1" sx={styles.sectionTitle} gutterBottom>
                            <CheckCircleIcon />
                            Confirme y genere los documentos
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            Se generarán {documentosSeleccionados.length * registrosValidos.length} documento(s)
                            en formato {opcionesImpresion.formato} ordenados por {opcionesImpresion.ordenamiento}.
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={generando ? <CircularProgress size={20} color="inherit" /> : <GetAppIcon />}
                            onClick={handleGenerarDocumentos}
                            disabled={generando || loading}
                            fullWidth
                            style={{ marginTop: 16 }}
                        >
                            {generando ? 'Generando Documentos...' : 'Generar y Descargar Documentos'}
                        </Button>
                    </Box>
                );
            default:
                return 'Paso desconocido';
        }
    };

    return (
        <Container maxWidth={false} sx={styles.root}>
            {/* Header */}
            <Box sx={styles.header}>
                <IconButton onClick={handleVolver} size="small" style={{ color: '#5c6bc0' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" style={{ fontWeight: 700, color: '#5c6bc0', display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                    <DescriptionIcon fontSize="large" />
                    Generar Documentos - Lote #{lote.Id}
                </Typography>
                {getEstadoChip()}
            </Box>

            {/* Información del lote */}
            <Paper sx={styles.loteInfo}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            Expediente Administrativo
                        </Typography>
                        <Typography variant="body1" style={{ fontWeight: 600 }}>
                            {lote.ExpedienteAdministrativo || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            Total Registros
                        </Typography>
                        <Typography variant="body1" style={{ fontWeight: 600 }}>
                            {lote.TotalRegistros || 0}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            Registros Válidos
                        </Typography>
                        <Typography variant="body1" style={{ fontWeight: 600, color: '#4caf50' }}>
                            {registrosValidos.length}
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            {/* Stepper */}
            <Stepper activeStep={activeStep} sx={styles.stepper}>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {/* Contenido */}
            <Grid container spacing={3} sx={styles.section}>
                <Grid size={{ xs: 12, lg: 8 }}>
                    <Paper style={{ padding: 24 }}>
                        {getStepContent(activeStep)}
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, lg: 4 }}>
                    <ResumenGeneracion
                        lote={lote}
                        documentosSeleccionados={documentosSeleccionados}
                        generando={generando}
                        progreso={progreso}
                    />
                </Grid>
            </Grid>

            {/* Barra de acciones */}
            <Box sx={styles.actionsBar}>
                <Button
                    onClick={handleVolver}
                    disabled={generando}
                    style={{ color: '#5c6bc0' }}
                >
                    Volver al Dashboard
                </Button>
                <Box display="flex" gap={2}>
                    {activeStep > 0 && (
                        <Button
                            onClick={handleBack}
                            disabled={generando}
                            style={{ color: '#5c6bc0' }}
                        >
                            Anterior
                        </Button>
                    )}
                    {activeStep < steps.length - 1 && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleNext}
                            disabled={loading}
                        >
                            Siguiente
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Dialog de finalización */}
            <Dialog
                open={confirmDialog.open}
                onClose={() => !generando && setConfirmDialog({ open: false })}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <CheckCircleIcon color="primary" />
                        Documentos Generados Correctamente
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography paragraph>
                        Los documentos se han generado exitosamente.
                    </Typography>

                    {/* Resumen de documentos generados por tipo */}
                    {documentosGenerados.length > 0 && (
                        <Box mb={2}>
                            <Box display="flex" alignItems="center" p={1.5} mb={1} style={{ backgroundColor: '#e8f5e9', borderRadius: 4 }}>
                                <DescriptionIcon style={{ color: '#4caf50', marginRight: 12 }} />
                                <Typography variant="body1" style={{ fontWeight: 600 }}>
                                    Cartas Poder generadas: {documentosGenerados.filter(d => d.tipo?.startsWith('CARTA_PODER')).length}
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" p={1.5} mb={1} style={{ backgroundColor: '#e8eaf6', borderRadius: 4 }}>
                                <ReceiptIcon style={{ color: '#5c6bc0', marginRight: 12 }} />
                                <Typography variant="body1" style={{ fontWeight: 600 }}>
                                    Recibos generados: {documentosGenerados.filter(d => d.tipo === 'RECIBO').length}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    <Typography variant="body2" color="text.secondary">
                        ¿Desea finalizar este lote de readjudicacion? Una vez finalizado,
                        no se podran realizar mas cambios.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog({ open: false })}>
                        No, continuar editando
                    </Button>
                    <Button
                        onClick={handleFinalizarLote}
                        color="primary"
                        variant="contained"
                        disabled={loading}
                    >
                        Si, Finalizar Lote
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog de resumen de finalización */}
            <Dialog
                open={!!resumenFinalizacion}
                onClose={() => {
                    setResumenFinalizacion(null);
                    navigate('/gestion-lotes?tab=readjudicacion');
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <CheckCircleIcon style={{ color: '#4caf50' }} />
                        Lote Finalizado Correctamente
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {resumenFinalizacion && (
                        <Box>
                            <Box display="flex" alignItems="center" p={1.5} mb={1} style={{ backgroundColor: '#e8f5e9', borderRadius: 4 }}>
                                <SwapHorizIcon style={{ color: '#4caf50', marginRight: 12 }} />
                                <Typography variant="body1">
                                    Expedientes procesados: <strong>{resumenFinalizacion.expedientesProcesados || 0}</strong>
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" p={1.5} mb={1} style={{ backgroundColor: '#e8f5e9', borderRadius: 4 }}>
                                <DescriptionIcon style={{ color: '#4caf50', marginRight: 12 }} />
                                <Typography variant="body1">
                                    Cartas Poder enviadas para firma: <strong>{documentosGenerados.filter(d => d.tipo?.startsWith('CARTA_PODER')).length}</strong>
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" p={1.5} mb={1} style={{ backgroundColor: '#e8eaf6', borderRadius: 4 }}>
                                <ReceiptIcon style={{ color: '#5c6bc0', marginRight: 12 }} />
                                <Typography variant="body1">
                                    Recibos adjuntados en expedientes: <strong>{resumenFinalizacion.recibosInsertados || 0}</strong>
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" p={1.5} mb={1} style={{ backgroundColor: '#fff3e0', borderRadius: 4 }}>
                                <LinkIcon style={{ color: '#e65100', marginRight: 12 }} />
                                <Typography variant="body1">
                                    Vinculaciones creadas: <strong>{resumenFinalizacion.vinculaciones || 0}</strong>
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" p={1.5} mb={1} style={{ backgroundColor: '#f3e5f5', borderRadius: 4 }}>
                                <NotificationsIcon style={{ color: '#7b1fa2', marginRight: 12 }} />
                                <Typography variant="body1">
                                    Notificaciones enviadas: <strong>{resumenFinalizacion.notificacionesEnviadas || 0}</strong>
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setResumenFinalizacion(null);
                            navigate('/gestion-lotes?tab=readjudicacion');
                        }}
                        color="primary"
                        variant="contained"
                    >
                        Volver al Dashboard
                    </Button>
                </DialogActions>
            </Dialog>

        </Container>
    );
};

export default LoteGenerarDocumentos;
